// ============================================================================
// COMPONENTE: PERFORMANCE WIDGET (V2.0 - DEEP ANALYTICS)
// ARQUIVO: src/components/PerformanceWidget.jsx
// OBJETIVO: Analisar histórico de simulados e detectar tendências de evolução.
// ============================================================================

import React, { useState, useEffect } from 'react';
import { auth } from '../shared/api/firebase/config';
import useCurrentUser from '../hooks/useCurrentUser';
import { fetchExamResults } from '../services/PerformanceWidgetRepository';
import { ACADEMIA_CONSTANTS } from '../features/academy/AcademiaConstants';
import { TrendingUp, TrendingDown, Minus, Target, AlertTriangle, Crosshair } from 'lucide-react';

const PerformanceWidget = ({ onAnalyze }) => {
    const { user, uid } = useCurrentUser();
    const [history, setHistory] = useState([]);
    const [stats, setStats] = useState({ 
        totalExams: 0, 
        avgScore: 0, 
        bestSubject: '---', 
        worstSubject: '---',
        trend: 'NEUTRAL' 
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadHistory = async () => {
            if (!user) { setLoading(false); return; }
            
            try {
                // Busca últimos 10 resultados para análise de tendência curta
                const data = await fetchExamResults(uid);
                
                if (data.length > 0) {
                    const totalExams = data.length;
                    const totalScore = data.reduce((acc, curr) => acc + curr.percentage, 0);
                    const avgScore = Math.round(totalScore / totalExams);
                    
                    // 1. CÁLCULO DE TENDÊNCIA (Últimos 3 vs Média Geral)
                    const recentAvg = data.slice(0, 3).reduce((acc, curr) => acc + curr.percentage, 0) / Math.min(3, data.length);
                    let trend = 'NEUTRAL';
                    if (recentAvg > avgScore + 2) trend = 'UP';
                    else if (recentAvg < avgScore - 2) trend = 'DOWN';

                    // 2. DETECTOR DE PONTOS FRACOS (WEAKNESS AI)
                    // Agrega todos os detalhes de todas as provas carregadas
                    const subjectStats = {}; // { 'ETICA': { hits: 10, total: 20 } }
                    
                    data.forEach(exam => {
                        if (exam.details) {
                            Object.entries(exam.details).forEach(([subj, det]) => {
                                if (!subjectStats[subj]) subjectStats[subj] = { hits: 0, total: 0 };
                                subjectStats[subj].hits += det.hits;
                                subjectStats[subj].total += det.total;
                            });
                        }
                    });

                    // Encontra a pior matéria (menor %) com relevância estatística (min 5 questões)
                    let worstSubj = null;
                    let minPercentage = 101;
                    
                    // Encontra a melhor matéria
                    let bestSubj = null;
                    let maxPercentage = -1;

                    Object.entries(subjectStats).forEach(([subj, det]) => {
                        if (det.total >= 5) { // Filtro de Relevância
                            const perc = (det.hits / det.total) * 100;
                            
                            if (perc < minPercentage) {
                                minPercentage = perc;
                                worstSubj = subj;
                            }
                            if (perc > maxPercentage) {
                                maxPercentage = perc;
                                bestSubj = subj;
                            }
                        }
                    });

                    setHistory(data);
                    setStats({ 
                        totalExams, 
                        avgScore, 
                        bestSubject: bestSubj ? ACADEMIA_CONSTANTS.SUBJECTS[bestSubj]?.label : '---', 
                        worstSubject: worstSubj ? ACADEMIA_CONSTANTS.SUBJECTS[worstSubj]?.label : '---',
                        trend 
                    });
                    
                    // Envia análise para o Pai (AcademiaArea) para recomendação de estudo
                    if (onAnalyze && worstSubj) {
                        onAnalyze({ worstSubject: worstSubj, percentage: Math.round(minPercentage) });
                    }
                }
            } catch (e) {
                console.error("Erro ao carregar performance:", e);
            } finally {
                setLoading(false);
            }
        };
        
        loadHistory();
    }, []);

    if (loading) return <div className="h-64 bg-white rounded-xl shadow-sm border border-gray-200 animate-pulse flex items-center justify-center"><div className="w-8 h-8 border-4 border-gray-200 border-t-[#C5A059] rounded-full animate-spin"></div></div>;
    
    if (!user) return (
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 text-center flex flex-col items-center justify-center h-full min-h-[250px]">
            <i className="fas fa-lock text-gray-300 text-3xl mb-2"></i>
            <h3 className="font-bold text-gray-700">Área do Aluno</h3>
            <p className="text-xs text-gray-500 mt-1">Faça login para salvar seu progresso.</p>
        </div>
    );

    if (history.length === 0) return (
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 text-center flex flex-col items-center justify-center h-full min-h-[250px]">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                <i className="fas fa-chart-line text-gray-400"></i>
            </div>
            <h3 className="font-bold text-gray-700 text-sm">Sem Dados de Performance</h3>
            <p className="text-xs text-gray-500 mt-1">Realize seu primeiro simulado para gerar métricas.</p>
        </div>
    );

    return (
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200 h-full flex flex-col relative overflow-hidden group min-h-[320px]">
            {/* Background Decorativo */}
            <div className="absolute top-0 right-0 p-6 opacity-5 pointer-events-none group-hover:scale-110 transition-transform duration-1000">
                <i className="fas fa-chart-area text-9xl text-brand-blue"></i>
            </div>

            {/* Cabeçalho */}
            <div className="flex justify-between items-start mb-6 z-10">
                <div>
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                        <Crosshair className="w-4 h-4 text-[#C5A059]"/> Sua Evolução
                    </h3>
                    <div className="flex items-end gap-2 mt-1">
                        <span className="text-4xl font-black text-brand-blue tracking-tighter">{stats.avgScore}%</span>
                        <div className={`flex items-center gap-1 text-xs font-bold mb-1.5 px-2 py-0.5 rounded-full ${
                            stats.trend === 'UP' ? 'bg-green-100 text-green-700' : 
                            stats.trend === 'DOWN' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                            {stats.trend === 'UP' && <TrendingUp className="w-3 h-3"/>}
                            {stats.trend === 'DOWN' && <TrendingDown className="w-3 h-3"/>}
                            {stats.trend === 'NEUTRAL' && <Minus className="w-3 h-3"/>}
                            {stats.trend === 'UP' ? 'Evoluindo' : stats.trend === 'DOWN' ? 'Atenção' : 'Estável'}
                        </div>
                    </div>
                </div>
                <div className="text-right">
                    <p className="text-[10px] font-bold text-gray-400 uppercase">Simulados</p>
                    <span className="text-xl font-black text-gray-700">{history.length}</span>
                </div>
            </div>

            {/* Insights Rápidos */}
            <div className="grid grid-cols-2 gap-4 mb-6 z-10">
                <div className="bg-green-50/50 p-3 rounded-lg border border-green-100">
                    <p className="text-[9px] font-bold text-green-700 uppercase mb-1 flex items-center gap-1"><Award className="w-3 h-3"/> Ponto Forte</p>
                    <p className="text-xs font-bold text-gray-800 truncate" title={stats.bestSubject}>{stats.bestSubject}</p>
                </div>
                <div className="bg-red-50/50 p-3 rounded-lg border border-red-100">
                    <p className="text-[9px] font-bold text-red-700 uppercase mb-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3"/> Atenção</p>
                    <p className="text-xs font-bold text-gray-800 truncate" title={stats.worstSubject}>{stats.worstSubject}</p>
                </div>
            </div>

            {/* Gráfico de Barras (Histórico Recente) */}
            <div className="mt-auto z-10">
                <div className="flex justify-between items-end mb-2">
                    <p className="text-[9px] font-bold text-gray-400 uppercase">Últimos 10 treinos</p>
                    <p className="text-[9px] font-bold text-[#C5A059] uppercase"><Target className="w-3 h-3 inline mr-1"/> Meta: 50%</p>
                </div>
                <div className="flex items-end gap-1 h-20 w-full bg-gray-50 rounded-lg p-2 border border-gray-100 relative">
                    {/* Linha de Meta (50%) */}
                    <div className="absolute left-0 right-0 bottom-[50%] h-px bg-[#C5A059]/30 border-t border-dashed border-[#C5A059] z-0"></div>
                    
                    {history.slice().reverse().map((exam, idx) => (
                        <div key={idx} className="flex-1 flex flex-col items-center group relative h-full justify-end z-10">
                            <div 
                                className={`w-full rounded-t-sm transition-all duration-500 relative ${
                                    exam.percentage >= 50 ? 'bg-green-500' : 'bg-red-400'
                                }`} 
                                style={{ height: `${exam.percentage}%` }}
                            >
                                {/* Tooltip */}
                                <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-[#20243B] text-white text-[9px] font-bold px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap shadow-xl pointer-events-none">
                                    {exam.percentage}%
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default PerformanceWidget;